<template>
  <div class="chat-list-wrapper">
    <!-- Header with create button -->
    <div v-if="canCreateChatGroup" class="chat-list-header">
      <h3>{{ title }}</h3>
      <button @click="$emit('create-chat-group')" class="create-btn">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
        </svg>
        New
      </button>
    </div>

    <!-- Chat List -->
    <div class="chat-list" v-if="chatGroups.length > 0">
      <div v-for="chat in chatGroups" :key="chat.id" class="chat-list-item"
        :class="{ 'has-unread': hasUnreadMessages(chat.id) }" @click="$emit('open-chat', chat)">
        <!-- Avatar -->
        <div class="chat-avatar">
          <TenantLogo logo-class="chat-logo" />
          <span v-if="hasUnreadMessages(chat.id)" class="unread-badge">
            {{ getUnreadCount(chat.id) > 99 ? '99+' : getUnreadCount(chat.id) }}
          </span>
        </div>

        <!-- Chat Info -->
        <div class="chat-info">
          <div class="chat-header-row">
            <span class="chat-name">{{ chat.name }}</span>
            <span v-if="chat.lastMessageTime" class="chat-time">
              {{ formatTime(chat.lastMessageTime) }}
            </span>
          </div>
          <p class="chat-preview">
            <span v-if="chat.lastMessage" class="last-message">
              <span v-if="chat.lastMessageSender" class="sender-name">
                {{ chat.lastMessageSender }}:
              </span>
              {{ truncateMessage(chat.lastMessage) }}
            </span>
            <span v-else class="no-messages">No messages yet</span>
          </p>
        </div>

        <!-- Edit Button (Admin/Coach only) -->
        <button v-if="canEditChatGroup" class="edit-btn" @click.stop="$emit('edit-chat-group', chat)"
          title="Edit chat group">
          <i class="bi bi-pencil-fill"></i>
        </button>

        <!-- Delete Button (Admin/Coach only) -->
        <button v-if="canEditChatGroup" class="delete-btn" @click.stop="$emit('delete-chat-group', chat)"
          title="Delete chat group">
          <i class="bi bi-trash-fill"></i>
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="#bcc0c4">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
        </svg>
      </div>
      <p>No team chats</p>
      <span v-if="canCreateChatGroup" class="empty-hint">
        Create a chat group to start messaging
      </span>
    </div>
  </div>
</template>

<script>
import TenantLogo from "@/components/TenantLogo/index.vue";

export default {
  name: "ChatList",
  components: {
    TenantLogo,
  },
  props: {
    chatGroups: {
      type: Array,
      default: () => [],
    },
    userData: {
      type: Object,
      default: () => ({}),
    },
    unreadCounts: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Team Chats",
    },
    // Club-level groups are admin-managed only; coaches keep create/edit
    // on their own team's groups.
    manageable: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["create-chat-group", "edit-chat-group", "delete-chat-group", "open-chat"],
  computed: {
    canCreateChatGroup() {
      return (
        this.manageable &&
        this.userData &&
        (this.userData.userType === "Admin" ||
          this.userData.userType === "Coach")
      );
    },
    canEditChatGroup() {
      return this.canCreateChatGroup;
    },
  },
  methods: {
    hasUnreadMessages(chatId) {
      return this.unreadCounts[chatId] && this.unreadCounts[chatId] > 0;
    },
    getUnreadCount(chatId) {
      return this.unreadCounts[chatId] || 0;
    },
    truncateMessage(message) {
      if (!message) return "";
      const maxLength = 50;
      if (message.length <= maxLength) return message;
      return message.substring(0, maxLength) + "...";
    },
    formatTime(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffDays === 0) {
        return date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else if (diffDays === 1) {
        return "Yesterday";
      } else if (diffDays < 7) {
        return date.toLocaleDateString("en-GB", { weekday: "short" });
      } else {
        return date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.chat-list-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.chat-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e4e6eb;
  background: #fff;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #050505;
  }
}

.create-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #1877f2;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #166fe5;
  }
}

.chat-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.chat-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid #f0f2f5;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    background: #f0f2f5;
  }

  &.has-unread {
    background: #f0f7ff;

    &:hover {
      background: #e7f3ff;
    }

    .chat-name {
      font-weight: 700;
    }

    .chat-preview {
      color: #050505;
      font-weight: 500;
    }
  }
}

.chat-avatar {
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;

  :deep(.chat-logo),
  :deep(img) {
    width: 48px !important;
    height: 48px !important;
    border-radius: 50%;
    object-fit: cover;
  }
}

.unread-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #e41e3f;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
}

.chat-info {
  flex: 1;
  min-width: 0;
  max-width: calc(100% - 60px);
  /* Account for avatar width + gap */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chat-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
}

.chat-name {
  font-size: 15px;
  font-weight: 600;
  color: #050505;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

.chat-time {
  font-size: 12px;
  color: #65676b;
  flex-shrink: 0;
  white-space: nowrap;
}

.chat-preview {
  margin: 0;
  font-size: 13px;
  color: #65676b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.sender-name {
  font-weight: 500;
}

.no-messages {
  font-style: italic;
  color: #8a8d91;
}

.edit-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  background: #f0f2f5;
  color: #65676b;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  i {
    font-size: 16px;
  }

  &:hover {
    background: #ffc107;
    color: #000;
  }
}

.delete-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  background: #f0f2f5;
  color: #65676b;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  i {
    font-size: 16px;
  }

  &:hover {
    background: #e41e3f;
    color: #fff;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;

  .empty-icon {
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 500;
    color: #65676b;
  }

  .empty-hint {
    font-size: 14px;
    color: #8a8d91;
  }
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .chat-list-header {
    padding: 10px 12px;

    h3 {
      font-size: 16px;
    }
  }

  .create-btn {
    padding: 6px 12px;
    font-size: 13px;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .chat-list-item {
    padding: 10px 12px;
    gap: 10px;
    max-width: 100%;
    overflow: hidden;
  }

  .chat-avatar {
    width: 44px;
    height: 44px;

    :deep(.chat-logo),
    :deep(img) {
      width: 44px !important;
      height: 44px !important;
    }
  }

  .chat-info {
    max-width: calc(100% - 140px);
    /* Smaller avatar + gap on mobile */
  }

  .chat-name {
    font-size: 14px;
  }

  .chat-preview {
    font-size: 12px;
  }

  .edit-btn {
    width: 36px;
    height: 36px;

    i {
      font-size: 14px;
    }
  }

  .empty-state {
    padding: 32px 16px;
  }
}
</style>
